import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { logger } from "../lib/logger";
import { toast } from "sonner";

function timeAgo(iso, t) {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return t("notif.justNow");
  if (m < 60) return `${m} ${t("notif.minAgo")}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ${t("notif.hAgo")}`;
  const d = Math.floor(h / 24);
  return `${d} ${t("notif.dAgo")}`;
}

function NotificationItem({ n, t, onClick }) {
  return (
    <button
      onClick={() => onClick(n)}
      className={`w-full text-left p-3 border-b border-[var(--ink-200)] last:border-b-0 hover:bg-[var(--surface-alt)] flex items-start gap-3 ${n.read ? "" : "bg-[var(--surface)]"}`}
      data-testid={`notification-${n.id}`}
    >
      {!n.read && <span className="mt-2 w-2 h-2 bg-[var(--brick)] flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[var(--ink)] leading-snug">
          {t(`notif.${n.type}`, { other: n.data?.other_party_name || "", listing: n.data?.listing_title || "" })}
        </div>
        <div className="label-tech mt-1">{timeAgo(n.created_at, t)}</div>
      </div>
    </button>
  );
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const knownIdsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      // Toast any fresh notifications (skip the very first load)
      if (knownIdsRef.current !== null) {
        const known = knownIdsRef.current;
        const fresh = data.filter((n) => !known.has(n.id) && !n.read);
        fresh.slice(0, 3).forEach((n) => {
          const msg = tRef.current(`notif.${n.type}`, {
            other: n.data?.other_party_name || "",
            listing: n.data?.listing_title || "",
          });
          toast(msg, { description: tRef.current("notif.toastNew") });
        });
      }
      knownIdsRef.current = new Set(data.map((n) => n.id));
      setItems(data);
    } catch (err) {
      logger.warn("notifications: load failed", err);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user, load]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;

  const unread = items.filter((i) => !i.read).length;

  const handleItemClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
      } catch (err) {
        logger.warn("notifications: mark-read failed", err);
      }
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.type && n.type.startsWith("collab_")) {
      navigate("/dashboard?tab=org");
    } else if (n.type && n.type.startsWith("user_invite_")) {
      navigate("/dashboard");
    } else {
      navigate("/dashboard");
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
    } catch (err) {
      logger.warn("notifications: mark-all-read failed", err);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 border border-[var(--ink-200)] hover:border-[var(--ink)] bg-[var(--surface)]"
        data-testid="notifications-bell-btn"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[var(--brick)] text-white font-mono-tech text-[10px] min-w-[18px] h-[18px] flex items-center justify-center px-1 border border-[var(--ink)]"
            data-testid="notifications-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] bg-[var(--bone)] border border-[var(--ink)] hard-shadow z-50"
          data-testid="notifications-dropdown"
        >
          <div className="p-3 border-b border-[var(--ink-200)] flex items-center justify-between">
            <div className="label-tech">{t("notif.title")}</div>
            {unread > 0 && (
              <button onClick={markAll} className="label-tech hover:text-[var(--ink)] underline inline-flex items-center gap-1" data-testid="notifications-mark-all-btn">
                <Check size={12} /> {t("notif.markAllRead")}
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-[var(--ink-500)]">{t("notif.empty")}</div>
            ) : (
              items.map((n) => (
                <NotificationItem key={n.id} n={n} t={t} onClick={handleItemClick} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
